import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, ListCell, NameCell, NumberCell } from '@/components/TableCells'
import { Blocklist } from '@/services/rules/blocklistsApi'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  ownerTeamName: 'team.name',
  iabCategoryAction: 'iabCategoryAction',
  objectState: 'objectState',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

interface BlocklistTableProps {
  blocklists: Blocklist[]
  loading?: boolean
  emptyMessage?: string
  selection?: Blocklist[]
  onSelectionChange?: (blocklists: Blocklist[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function BlocklistTable({
  blocklists,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: BlocklistTableProps) {
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const ruleCount = (b: Blocklist) =>
    b.advertisers.length + b.advertiserDomains.length + b.iabCategories.length + b.creatives.length + b.lineItems.length

  const columns: ColumnDef<Blocklist, unknown>[] = [
    ...(selectable ? [selectionColumn<Blocklist>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'ownerTeamName',
      header: t('common.team'),
      meta: { minWidth: 140 },
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {row.original.ownerTeamName || '—'}
        </span>
      ),
    },
    {
      id: 'rules',
      header: t('blocklists.rules'),
      enableSorting: false,
      size: 80,
      cell: ({ row }) => (
        <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
          {ruleCount(row.original)}
        </span>
      ),
    },
    {
      accessorKey: 'iabCategoryAction',
      header: t('blocklists.action'),
      size: 120,
      cell: ({ row }) => (
        row.original.iabCategoryAction ? (
          <span
            className={`chip chip--${row.original.iabCategoryAction === 'BLOCKED' ? 'rejected' : 'running'}`}
            style={{ fontSize: 11 }}
          >
            <span className="dot" />
            {row.original.iabCategoryAction}
          </span>
        ) : (
          <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>—</span>
        )
      ),
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => (
        <span
          className={`chip chip--${row.original.objectState === 'ACTIVE' ? 'running' : 'draft'}`}
          style={{ fontSize: 11 }}
        >
          <span className="dot" />
          {row.original.objectState}
        </span>
      ),
    },
    optionalColumn<Blocklist>({
      id: 'advertisers',
      header: t('blocklists.advertisersBlocked'),
      accessorFn: (row) => row.advertisers?.length ?? 0,
      size: 130,
      cell: ({ row }) => <NumberCell value={row.original.advertisers?.length ?? 0} />,
    }),
    optionalColumn<Blocklist>({
      id: 'advertiserDomains',
      header: t('blocklists.advertiserDomains'),
      enableSorting: false,
      meta: { minWidth: 170 },
      cell: ({ row }) => <ListCell values={row.original.advertiserDomains} />,
    }),
    optionalColumn<Blocklist>({
      id: 'iabCategories',
      header: t('blocklists.iabCategories'),
      enableSorting: false,
      meta: { minWidth: 160 },
      cell: ({ row }) => <ListCell values={row.original.iabCategories} />,
    }),
    optionalColumn<Blocklist>({
      id: 'creatives',
      header: t('creatives.title'),
      accessorFn: (row) => row.creatives?.length ?? 0,
      size: 110,
      cell: ({ row }) => <NumberCell value={row.original.creatives?.length ?? 0} />,
    }),
    optionalColumn<Blocklist>({
      id: 'lineItems',
      header: t('lineItems.title'),
      accessorFn: (row) => row.lineItems?.length ?? 0,
      size: 110,
      cell: ({ row }) => <NumberCell value={row.original.lineItems?.length ?? 0} />,
    }),
    optionalColumn<Blocklist>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<Blocklist>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={blocklists}
      loading={loading}
      emptyMessage={emptyMessage ?? 'No blocklists found.'}
      columnsKey="blocklists"
      rowHref={(row) => `/publishing/blocklists/${row.id}`}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
